import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { inputFormatters } from '@blend/ui-common';
import { colors, fonts, LinkButton, media, mixins, SimpleDataTable } from '@blend/consumer-components';

import * as deploymentConfig from 'login-utils/deploymentConfig';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import { getOrganizationColors } from 'login-utils/deploymentConfig';
import { UpfrontRate } from '../upfrontRatesUtils';

export interface UpfrontRatesTableProps {
  hasError?: boolean;
  loading?: boolean;
  upfrontRates?: UpfrontRate[];
  onRefreshClick: any;
}

const { MONEY, PERCENTAGE } = inputFormatters;

const actionColor = deploymentConfig.getOrganizationColors().action;

const styles = StyleSheet.create({
  status: {
    ...mixins.paddingHorizontal(24),
    alignItems: 'center',
    background: colors.contentOverlayWhite,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    textAlign: 'center',

    [media.mobileMedium]: {
      ...mixins.paddingHorizontal(8),
    },
  },
  heading: {
    ...mixins.marginVertical(8),
    color: colors.darkerGray,
    fontWeight: fonts.weight.bold,
  },
  bodyText: {
    color: colors.darkerGray,
    fontSize: 14,
  },
  loadingSpinner: {
    marginBottom: 16,
  },
});

// Exported for testing only
export function getUpfrontRatesTableConfig(upfrontRates: UpfrontRate[]): SimpleDataTable.Config {
  return {
    headings: [{ content: 'LOAN TYPE' }, { content: 'PER MONTH' }, { content: 'RATE/APR' }],
    rows: upfrontRates.map(({ apr, displayName, interestRate, monthlyPayment }) => {
      const formattedMonthlyPayment = MONEY.formatRoundedWithDollarSign(monthlyPayment);
      const formattedInterestRate = PERCENTAGE.format(interestRate.toFixed(3), undefined, { alwaysKeepDecimal: true });
      const formattedApr = PERCENTAGE.format(apr.toFixed(3), undefined, { alwaysKeepDecimal: true });

      return {
        cells: [
          { content: displayName },
          { content: formattedMonthlyPayment },
          { content: `${formattedInterestRate}/${formattedApr}` },
        ],
      };
    }),
  };
}

/**
 * Displays a list of UpfrontRate products in a SimpleDataTable
 */
export default class UpfrontRatesTable extends React.PureComponent<UpfrontRatesTableProps> {
  renderEmptyStatus = () => (
    <div className={css(styles.status)}>
      <p className={css(styles.heading)}>Looks like we don't have rates to show you right now</p>
      <p className={css(styles.bodyText)}>But you can go ahead and finish the application. We'll be in touch soon!</p>
    </div>
  );

  renderErrorStatus = () => {
    const { onRefreshClick } = this.props;
    return (
      <div className={css(styles.status)}>
        <p className={css(styles.heading)}>Looks like we don't have rates to show you right now</p>
        <p className={css(styles.bodyText)}>
          <LinkButton color={actionColor} onClick={onRefreshClick}>
            Try again
          </LinkButton>{' '}
          or go ahead and finish the application. We'll be in touch soon!
        </p>
      </div>
    );
  };

  renderLoadingStatus = () => (
    <div className={css(styles.status)}>
      <div className={css(styles.loadingSpinner)}>
        <LoadingSpinner />
      </div>
      <p>Sit tight! Checking rates can take a minute.</p>
    </div>
  );

  render() {
    const { hasError, loading, upfrontRates = [] } = this.props;
    const tableConfig = getUpfrontRatesTableConfig(upfrontRates);
    const organizationColors = getOrganizationColors();
    const selectedRowColor = organizationColors.loginBackground;

    return (
      <SimpleDataTable
        caption="Here are some personal rates based on the info you entered"
        emptyStatus={this.renderEmptyStatus()}
        errorStatus={hasError && this.renderErrorStatus()}
        loadingStatus={loading && this.renderLoadingStatus()}
        tableConfig={tableConfig}
        selectedRowColor={selectedRowColor}
      />
    );
  }
}
